// HOOKS
import { useState, createContext, useEffect } from "react";
import useQuery from "../../../hooks/other/useQuery.other.hook";
import { useLazyQuery, useMutation } from "@apollo/client";
import useUpdatePermission from "../../../hooks/users/permissions/useUpdate.permission.user.hook";
import useDeleteManyPermissions from "../../../hooks/users/permissions/useDeleteMany.permission.user.hook";
import useCreatePermission from "../../../hooks/users/permissions/useCreate.permission.user.hook";
// GRAPHQL
import { GET_PERMISSIONS } from "../../../graphql/queries/parts/permissions.users.queries";
import { UPDATE_PERMISSION } from "../../../graphql/mutations/users/permissions.users.mutation";

const DEFAULT_QUERY = {
  page: 1,
  limit: 25,
  sort: "name",
  search: "",
};

export const PermissionsContext = createContext({
  permissions: [],
  setPermissions: () => {},
  metaData: {},
  setMetaData: () => {},
  isLoading: false,
  setIsLoading: () => {},
  query: {},
  setQuery: () => {},
  permissionsInput: {},
  setPermissionsInput: () => {},
  fetchPermissions: () => {},
  updatePermission: () => {},
  deletePermission: () => {},
  createPermission: () => {},
  handleLimitChange: () => {},
  handlePageChange: () => {},
  handleSearch: () => {},
  handleSort: () => {},
  handleChange: () => {},
});

const findPermission = (permissions, id) => {
  return permissions.find((permission) => permission._id === id);
};

export const PermissionsProvider = ({ children }) => {
  const {
    query,
    setQuery,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    handleSort,
  } = useQuery({ defaultQuery: DEFAULT_QUERY });
  const { deleteManyPermissions: deleteManyPermissionHook } =
    useDeleteManyPermissions();
  const { createPermission: createPermissionHook } = useCreatePermission();
  const { updatePermission: updatePermissionHook } = useUpdatePermission();
  // STATE
  const [permissions, setPermissions] = useState([]);
  const [metaData, setMetaData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [permissionsInput, setPermissionsInput] = useState({});
  const [timedUpdate, setTimedUpdate] = useState(null);
  //QUERIES
  const [getPermissions] = useLazyQuery(GET_PERMISSIONS, {
    fetchPolicy: "no-cache",
  });

  // EFFECTS
  // Set page to last page if current page is greater than total pages
  useEffect(() => {
    if (metaData.totalPages < query.page)
      setQuery({ ...query, page: metaData.totalPages });
  }, [metaData]);

  // Fetch permissions on query change
  useEffect(() => {
    fetchPermissions();
  }, [query]);
  // METHODS
  const fetchPermissions = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await getPermissions({
        variables: {
          query: query || {},
          permissionsInput: permissionsInput || {},
        },
      });
      if (data?.permissions.permissions) {
        setPermissions(data.permissions.permissions);
        setMetaData(data.permissions.metaData);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error({ error });
    }
  };

  const updatePermission = async (permission) => {
    try {
      if (timedUpdate) clearTimeout(timedUpdate);
      const timeout = setTimeout(async () => {
        const updatedPermission = await updatePermissionHook(permission);
      }, 1000);
      setTimedUpdate(timeout);
    } catch (error) {
      console.error({ error });
      alert(`Error updating permission: ${error.message}`);
    }
  };

  const deletePermissions = async (permissions) => {
    try {
      const deletedPermissions = await deleteManyPermissionHook(permissions);
    } catch (error) {
      console.error({ error });
      alert(`Error deleting permissions: ${error.message}`);
    }
  };

  const createPermission = async (permission) => {
    try {
      const createdPermission = await createPermissionHook(permission);
      return createdPermission;
    } catch (error) {
      console.error({ error });
      alert(`Error creating permission: ${error.message}`);
    }
  };

  const handleChange = async (event) => {
    const { name, value, dataset } = event.target;
    const { id } = dataset;
    const existingPermission = findPermission(permissions, id);
    setPermissions(
      permissions.map((permission) =>
        permission._id === existingPermission._id
          ? { ...permission, [name]: value }
          : permission
      )
    );
    await updatePermission({ ...existingPermission, [name]: value });
  };
  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        setPermissions,
        metaData,
        setMetaData,
        isLoading,
        setIsLoading,
        query,
        setQuery,
        permissionsInput,
        setPermissionsInput,
        fetchPermissions,
        updatePermission,
        deletePermissions,
        createPermission,
        handleLimitChange,
        handlePageChange,
        handleSearch,
        handleSort,
        handleChange,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};
